import { useCallback, useState } from 'react';

import { SerialEnforcementLevel } from './constants';
import { useHasMissingRequiredSerials } from './use-has-missing-required-serials';
import { useSerialEnforcementLevel } from './use-serial-enforcement-level';

import type { SerializableLineItem } from './use-has-missing-required-serials';

export type SerialPrintGuard = {
  /** True when the print action should be disabled outright (Block + a required serial is missing). */
  isPrintBlocked: boolean;
  /** True while the dismissable Warn confirmation is open — render <SerialWarningModal> bound to confirmProceed/dismiss. */
  isWarningModalOpen: boolean;
  /**
   * Wrap a print action with this before dispatching it. Under Block+missing it does
   * nothing (the caller should already disable the button via isPrintBlocked). Under
   * Warn+missing it opens the warning modal and defers `proceed` until confirmed.
   * Otherwise (Allow, or no missing serials) it calls `proceed` immediately.
   */
  guardPrint: (proceed: () => void) => void;
  confirmWarningAndProceed: () => void;
  dismissWarning: () => void;
};

// Shared gate consumed by every print entry point (scan page, order details
// create/reprint/direct-print) so Block/Warn/Allow behave identically everywhere.
export const useSerialPrintGuard = (items: readonly SerializableLineItem[]): SerialPrintGuard => {
  const level = useSerialEnforcementLevel();
  const hasMissingRequiredSerials = useHasMissingRequiredSerials(items);
  const [pendingProceed, setPendingProceed] = useState<(() => void) | null>(null);

  const isPrintBlocked = level === SerialEnforcementLevel.Block && hasMissingRequiredSerials;

  const guardPrint = useCallback(
    (proceed: () => void) => {
      if (level === SerialEnforcementLevel.Block && hasMissingRequiredSerials) {
        return;
      }
      if (level === SerialEnforcementLevel.Warn && hasMissingRequiredSerials) {
        setPendingProceed(() => proceed);
        return;
      }
      proceed();
    },
    [level, hasMissingRequiredSerials],
  );

  const confirmWarningAndProceed = useCallback(() => {
    const proceed = pendingProceed;
    setPendingProceed(null);
    proceed?.();
  }, [pendingProceed]);

  const dismissWarning = useCallback(() => {
    setPendingProceed(null);
  }, []);

  return {
    isPrintBlocked,
    isWarningModalOpen: pendingProceed != null,
    guardPrint,
    confirmWarningAndProceed,
    dismissWarning,
  };
};
